import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { AgentDisplay, AgentReplyResult, ConnectHttpAdapter, MessageDisplay } from '@rcs/ers-connect';
import { ersConstants, generateGetUrlFromConfigKeyFn, generatePostUrlFromConfigKeyFn } from '@rcs/utils';
import { environment } from '../../../../environments/environment';

/**
 * App-level implementation of ConnectHttpAdapter.
 *
 * Bridges the connect library to the ERS app's environment configuration.
 *
 * Provided at root level via app.module.ts:
 * ```ts
 * { provide: ConnectHttpAdapter, useClass: AppConnectHttpAdapter }
 * ```
 */
@Injectable()
export class AppConnectHttpAdapter extends ConnectHttpAdapter {
  private _http = inject(HttpClient);
  constructor(@Inject(PLATFORM_ID) private readonly _platformId: object) {
    super();
  }

  getIdentityApiUrl(): string {
    return environment.apiUrl.identity;
  }

  getDocumentUploadApiUrl(): string {
    return environment.apiUrl.documentUpload;
  }

  buildDocumentUrl(fileUrl: string): string {
    const normalizedPath = this._normalizeAttachmentPath(fileUrl);
    const sourceUrl = this._toAttachmentSourceUrl(normalizedPath);

    return this._buildDownloadProxyUrl(sourceUrl);
  }

  buildMediaSourceUrl(relativePath: string): string {
    if (/^https?:\/\//i.test(relativePath)) {
      return relativePath;
    }
    const normalizedPath = this._normalizeAttachmentPath(relativePath);
    return this._buildMediaUrl(normalizedPath);
  }

  downloadDocument(documentUrl: string): Observable<Blob> {
    const resolvedUrl = /^https?:\/\//i.test(documentUrl)
      ? documentUrl
      : this.buildDocumentUrl(documentUrl);

    return this._http.get(resolvedUrl, {
      responseType: 'blob',
      headers: this._buildDownloadHeaders(),
    });
  }

  getFromIdentity<T>(path: string): Observable<T> {
    const url = `${this.getIdentityApiUrl()}${path}`;
    return this._http.get<T>(url);
  }

  listAgents(tenantId: string): Observable<AgentDisplay[]> {
    const url = generateGetUrlFromConfigKeyFn(
      'fetchBots',
      [{ tenant_id: tenantId }],
      null,
      environment.apiUrl,
      environment.isOfflineHosted,
    );

    return this._http.get<any>(url, {
      headers: this._buildGeraltJwtHeaders(),
    }).pipe(
      map((response) => {
        const bots = Array.isArray(response)
          ? response
          : response?.tokens
            ?? response?.bots
            ?? response?.bot_tokens
            ?? response?.data?.tokens
            ?? response?.data?.bots
            ?? response?.data?.bot_tokens
            ?? response?.data
            ?? response?.value
            ?? [];

        if (!Array.isArray(bots)) {
          const message = response?.message?.trim?.() || response?.error?.trim?.() || '';
          if (message) {
            throw new Error(message);
          }
          return [] as AgentDisplay[];
        }

        if (bots.length === 0) {
          const message = response?.message?.trim?.() || '';
          if (message) {
            throw new Error(message);
          }
        }

        return bots.map((bot: any) => ({
          id: bot?.bot_token ?? bot?.id ?? bot?._id ?? bot?.bot_id,
          name: bot?.bot_name ?? bot?.name ?? 'AI Agent',
          botToken: bot?.bot_token ?? '',
          avatarUrl: bot?.icon_url ?? bot?.icon_file_path ?? null,
          description: bot?.description ?? null,
        })) as AgentDisplay[];
      })
    );
  }

  queryAgent(botToken: string, query: string, conversationId?: string | null): Observable<AgentReplyResult> {
    if (isPlatformBrowser(this._platformId)) {
      localStorage.setItem(ersConstants.BOT_TOKEN, botToken);
    }

    const url = generatePostUrlFromConfigKeyFn(
      'searchBotQuery',
      'SearchBotQuery',
      environment.apiUrl,
      null,
      environment.isOfflineHosted,
    );

    const body: any = { query };
    if (conversationId) {
      body.conversation_id = conversationId;
    }

    return this._http.post<any>(url, body, {
      headers: this._buildGeraltBotQueryHeaders(botToken),
    }).pipe(
      map((response) => this._extractAgentReply(response))
    );
  }

  getAgentConversation(botToken: string, conversationId: string): Observable<MessageDisplay[]> {
    const url = generateGetUrlFromConfigKeyFn(
      'fetchBotConversation',
      [{ bot_token: botToken, conversation_id: conversationId }],
      null,
      environment.apiUrl,
      environment.isOfflineHosted,
    );

    return this._http.get<any>(url, {
      headers: this._buildGeraltBotQueryHeaders(botToken),
    }).pipe(
      map((response) => this._extractConversationMessages(response, botToken))
    );
  }

  registerBotInChatService(botToken: string, botExternalId: string): Observable<{ botChatId: string }> {
    const url = `${environment.apiUrl.chat}Bot/savebotdetailsandoptions`;
    const body = {
      id: null,
      botExternalId,
      botToken,
      isPersonalityChecked: false,
      isColorChecked: false,
      isWelcomeMessageChecked: false,
      selectedNotificationOption: false,
      selectedAlertOption: false,
      modifiedBy: this._getCurrentUserId(),
    };

    return this._http.post<any>(url, body, {
      headers: this._buildChatServiceHeaders(),
    }).pipe(
      map((response) => {
        const id = response?.value?.id ?? response?.id ?? null;
        if (!id) {
          throw new Error(response?.value?.message ?? 'Failed to register bot in Chat service');
        }
        return { botChatId: id };
      })
    );
  }

  linkBotToUser(botChatId: string, userId: string): Observable<any> {
    const url = `${environment.apiUrl.chat}Bot/savebotforchat`;
    const body = {
      botId: botChatId,
      userId,
      modifiedBy: userId,
    };

    return this._http.post<any>(url, body, {
      headers: this._buildChatServiceHeaders(),
    });
  }

  getBotChatMessages(userId: string, botChatId: string, pageNumber = 1, pageSize = 500): Observable<MessageDisplay[]> {
    const url = `${environment.apiUrl.chat}Bot/getallpaginatedbotchatsbyuseridandbotid`;
    const body = {
      userId,
      botId: botChatId,
      pageSize,
      pageNumber,
    };

    return this._http.post<any>(url, body, {
      headers: this._buildChatServiceHeaders(),
    }).pipe(
      map((response) => {
        const messages = Array.isArray(response) ? response : response?.data ?? response?.value ?? [];
        if (!Array.isArray(messages)) return [];

        const mapped = messages
          .map((msg: any) => {
            // isBotMessage is the CONFIRMED field (per the backend team's
            // client-changes spec for this endpoint) that distinguishes the
            // agent's own reply from the human's message — both used to
            // carry the same userId, since saveBotAgentMessage has no other
            // way to save "as the bot" than by writing that same userId's
            // row (see its own comment). Until this field is deployed to a
            // given environment it's simply absent from the response, so
            // the other guesses (isBot/fromBot/senderType/role — left over
            // from before it was confirmed) and the userId/senderId
            // comparison stay as a fallback rather than being deleted
            // outright, so existing environments don't regress to "every
            // message on my side" the moment this ships to code but not yet
            // to every backend environment.
            const explicitIsBot = msg.isBotMessage ?? msg.isBot ?? msg.fromBot
              ?? (typeof msg.senderType === 'string' ? msg.senderType.toLowerCase() === 'bot' : undefined)
              ?? (typeof msg.role === 'string' ? msg.role.toLowerCase() === 'bot' || msg.role.toLowerCase() === 'assistant' : undefined);
            const isOwn = explicitIsBot !== undefined
              ? !explicitIsBot
              : (msg.userId === userId || msg.senderId === userId);
            return {
              id: msg.id ?? msg.chatId,
              senderId: msg.userId ?? msg.senderId,
              senderName: isOwn ? 'You' : (msg.senderName ?? 'AI Agent'),
              content: msg.content ?? msg.message ?? '',
              type: (msg.messageType ?? 'text') as any,
              isOwn,
              // The backend's client-changes spec confirms this endpoint now
              // ships with createdOn (when available). If present, use it;
              // otherwise fall back through other timestamp field names.
              // DO NOT fall back to new Date() — that causes timestamps to change
              // on every reload. If the backend doesn't return a timestamp, the
              // message is likely a local/transient one and should show no time.
              createdAt: msg.createdOn ?? msg.createdAt ?? msg.createdDate ?? '',
              isEdited: msg.isEdited ?? false,
            } as MessageDisplay;
          });

        // The backend's getallpaginatedbotchatsbyuseridandbotid returns
        // messages in descending order (newest first) for efficient
        // pagination. The component's _loadMessages expects ascending order
        // (oldest first) so messages display chronologically from top to
        // bottom and persistedMessages[length-1] is the latest. Reverse
        // here to match that expectation.
        return mapped.reverse();
      })
    );
  }

  uploadFile(file: File, tenantId: string, folder: string): Observable<{ fileUrl: string; fileName: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.getDocumentUploadApiUrl()}Profile/AttachDocument?tenantId=${encodeURIComponent(tenantId)}&folder=${folder}`;

    return this._http.post<any>(url, formData).pipe(
      map((response) => ({
        fileUrl: response?.value?.documentUrl || response?.value?.documentPath || response?.fileUrl || response?.documentUrl || '',
        fileName: response?.value?.documentName || response?.fileName || file.name,
      }))
    );
  }

  private _normalizeAttachmentPath(path: string): string {
    return path
      .replace(/(^|\/)chat-attachments(?=\/|$)/gi, '$1userChatAttachments')
      .replace(/(^|\/)group-chat-attachments(?=\/|$)/gi, '$1groupChatAttachments');
  }

  private _toAttachmentSourceUrl(path: string): string {
    const mediaUrl = this._toMediaStorageUrl(path);
    if (mediaUrl) {
      return mediaUrl;
    }

    const base = this.getDocumentUploadApiUrl();
    const normalizedBase = base.endsWith('/') ? base : `${base}/`;
    const trimmedPath = path.startsWith('/') ? path.slice(1) : path;

    return `${normalizedBase}${trimmedPath}`;
  }

  private _toMediaStorageUrl(path: string): string | null {
    const normalizedPath = path.replace(/\\/g, '/');

    if (/^https?:\/\/media\.coreaxis360\.com\//i.test(normalizedPath)) {
      return normalizedPath;
    }

    const absoluteMatch = normalizedPath.match(/^https?:\/\/[^/]+\/api\/([^/]+\/[^/]+\/[^/]+(?:\/[^?#]+)?)$/i);
    if (absoluteMatch?.[1]) {
      return this._buildMediaUrl(absoluteMatch[1]);
    }

    const relativeMatch = normalizedPath.match(/^([^/]+\/[^/]+\/[^/]+(?:\/[^?#]+)?)$/);
    if (relativeMatch?.[1] && this._looksLikeAttachmentPath(relativeMatch[1])) {
      return this._buildMediaUrl(relativeMatch[1]);
    }

    return null;
  }

  private _buildMediaUrl(storagePath: string): string {
    const trimmedPath = storagePath.replace(/^\/+/, '');
    return `https://media.coreaxis360.com/iersblobcontainer/${this._getBlobEnvironmentSegment()}/${trimmedPath}`;
  }

  private _looksLikeAttachmentPath(path: string): boolean {
    const segments = path.split('/').filter(Boolean);
    return segments.length >= 3;
  }

  private _getBlobEnvironmentSegment(): string {
    const base = this.getDocumentUploadApiUrl().toLowerCase();

    if (base.includes('dusvcdev')) {
      return 'iersdev';
    }

    if (base.includes('dusvcqa')) {
      return 'iersqa';
    }

    return 'iersprod';
  }

  private _buildDownloadProxyUrl(sourceUrl: string): string {
    return generateGetUrlFromConfigKeyFn(
      'lmDownloadPolicyDocument' as any,
      [{ url: encodeURIComponent(sourceUrl) }],
      null,
      environment.apiUrl,
      environment.isOfflineHosted,
    );
  }

  private _buildDownloadHeaders(): HttpHeaders | undefined {
    if (!isPlatformBrowser(this._platformId)) {
      return undefined;
    }

    const token = localStorage.getItem(ersConstants.ERS_JWT_TOKEN);
    if (!token) {
      return undefined;
    }

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  private _buildGeraltJwtHeaders(): HttpHeaders | undefined {
    const jwtToken = this._getJwtToken();
    if (!jwtToken) {
      return undefined;
    }

    return new HttpHeaders({
      Authorization: `Bearer ${jwtToken}`,
    });
  }

  private _buildGeraltBotQueryHeaders(botToken: string): HttpHeaders | undefined {
    const jwtToken = this._getJwtToken();
    let headers = new HttpHeaders({
      Authorization: botToken,
    });

    if (jwtToken) {
      headers = headers.set('jwt-authorization', `Bearer ${jwtToken}`);
    }

    return headers;
  }

  private _getJwtToken(): string | null {
    if (!isPlatformBrowser(this._platformId)) {
      return null;
    }

    return localStorage.getItem(ersConstants.ERS_JWT_TOKEN);
  }

  private _buildChatServiceHeaders(): HttpHeaders | undefined {
    const jwtToken = this._getJwtToken();
    if (!jwtToken) return undefined;
    return new HttpHeaders({ Authorization: `Bearer ${jwtToken}` });
  }

  private _getCurrentUserId(): string {
    if (!isPlatformBrowser(this._platformId)) return '';
    try {
      const tenant = localStorage.getItem('SELECTED_TENANT');
      if (tenant) {
        const parsed = JSON.parse(tenant);
        if (parsed.userId) return parsed.userId;
      }
      const token = this._getJwtToken();
      if (!token) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId ?? payload.id ?? payload.sub ?? payload.LoginId ?? '';
    } catch {
      return '';
    }
  }

  private _extractAgentReply(response: any): AgentReplyResult {
    const content = this._extractReplyText(response)?.trim() || 'No response received from the selected agent.';
    const conversationId = response?.conversation_id
      ?? response?.conversationId
      ?? response?.data?.conversation_id
      ?? response?.data?.conversationId
      ?? response?.message?.conversation_id
      ?? null;

    return { content, conversationId };
  }

  private _extractReplyText(response: any): string {
    const directCandidates = [
      response?.response,
      response?.answer,
      response?.content,
      response?.message?.response,
      response?.message?.content,
      response?.data?.response,
      response?.data?.content,
      response?.result?.response,
      response?.result?.content,
    ];

    for (const candidate of directCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }

    const messages = Array.isArray(response?.messages)
      ? response.messages
      : Array.isArray(response?.data?.messages)
        ? response.data.messages
        : [];

    const assistantMessage = [...messages].reverse().find((message: any) => message?.role === 'assistant');
    if (assistantMessage) {
      if (typeof assistantMessage?.response === 'string' && assistantMessage.response.trim()) {
        return assistantMessage.response;
      }
      if (typeof assistantMessage?.content === 'string' && assistantMessage.content.trim()) {
        return assistantMessage.content;
      }
    }

    return '';
  }

  private _extractConversationMessages(response: any, botToken: string): MessageDisplay[] {
    const messages = Array.isArray(response)
      ? response
      : response?.messages
        ?? response?.conversation
        ?? response?.data?.messages
        ?? response?.data?.conversation
        ?? response?.data
        ?? [];

    if (!Array.isArray(messages)) {
      return [];
    }

    const currentUserId = this._getJwtToken() ? 'current_user' : '';

    return messages.map((msg: any, index: number) => {
      const role = msg?.role ?? msg?.type ?? 'user';
      const isUserMessage = role === 'user' || role === 'human';
      const content = msg?.content ?? msg?.response ?? msg?.message ?? msg?.text ?? '';
      const timestamp = msg?.timestamp ?? msg?.created_at ?? msg?.createdAt ?? new Date().toISOString();

      return {
        id: msg?.id ?? msg?._id ?? `geralt_msg_${index}`,
        senderId: isUserMessage ? currentUserId : botToken,
        senderName: isUserMessage ? 'You' : (msg?.bot_name ?? 'AI Agent'),
        content: typeof content === 'string' ? content : JSON.stringify(content),
        type: 'text',
        isOwn: isUserMessage,
        createdAt: timestamp,
        isEdited: false,
      } as MessageDisplay;
    });
  }
}
